import { useAnimation, useSelectedObject } from '@/context/AnimationContext';
import { useI18n } from '@/context/I18nContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Sparkles, ArrowDown, RotateCw, Zap, Eye, EyeOff, MoveRight } from 'lucide-react';

const presetDefs = [
  {
    id: 'bounce',
    icon: ArrowDown,
    color: 'text-green-400',
    generate: (obj, totalFrames) => {
      const mid = Math.floor(totalFrames / 2);
      return [
        { frame: 0, x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 1 },
        { frame: Math.floor(mid * 0.5), x: obj.x, y: obj.y - 80, rotation: obj.rotation || 0, scaleX: (obj.scaleX || 1) * 1.05, scaleY: (obj.scaleY || 1) * 0.95, opacity: 1 },
        { frame: mid, x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: obj.scaleX || 1, scaleY: (obj.scaleY || 1) * 1.08, opacity: 1 },
        { frame: Math.floor(mid * 1.5), x: obj.x, y: obj.y - 40, rotation: obj.rotation || 0, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 1 },
        { frame: totalFrames, x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 1 },
      ];
    }
  },
  {
    id: 'fadeIn',
    icon: Eye,
    color: 'text-blue-400',
    generate: (obj, totalFrames) => [
      { frame: 0, x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: (obj.scaleX || 1) * 0.5, scaleY: (obj.scaleY || 1) * 0.5, opacity: 0 },
      { frame: totalFrames, x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 1 },
    ]
  },
  {
    id: 'fadeOut',
    icon: EyeOff,
    color: 'text-purple-400',
    generate: (obj, totalFrames) => [
      { frame: 0, x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 1 },
      { frame: totalFrames, x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: (obj.scaleX || 1) * 0.5, scaleY: (obj.scaleY || 1) * 0.5, opacity: 0 },
    ]
  },
  {
    id: 'spin',
    icon: RotateCw,
    color: 'text-amber-400',
    generate: (obj, totalFrames) => {
      const q = Math.floor(totalFrames / 4);
      return [
        { frame: 0, x: obj.x, y: obj.y, rotation: 0, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 1 },
        { frame: q, x: obj.x, y: obj.y, rotation: 90, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 1 },
        { frame: q * 2, x: obj.x, y: obj.y, rotation: 180, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 1 },
        { frame: q * 3, x: obj.x, y: obj.y, rotation: 270, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 1 },
        { frame: totalFrames, x: obj.x, y: obj.y, rotation: 360, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 1 },
      ];
    }
  },
  {
    id: 'pulse',
    icon: Zap,
    color: 'text-rose-400',
    generate: (obj, totalFrames) => {
      const s = obj.scaleX || 1;
      const sy = obj.scaleY || 1;
      const step = Math.floor(totalFrames / 4);
      return [
        { frame: 0, x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: s, scaleY: sy, opacity: 1 },
        { frame: step, x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: s * 1.15, scaleY: sy * 1.15, opacity: 1 },
        { frame: step * 2, x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: s, scaleY: sy, opacity: 1 },
        { frame: step * 3, x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: s * 1.15, scaleY: sy * 1.15, opacity: 1 },
        { frame: totalFrames, x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: s, scaleY: sy, opacity: 1 },
      ];
    }
  },
  {
    id: 'slideIn',
    icon: MoveRight,
    color: 'text-cyan-400',
    generate: (obj, totalFrames) => [
      { frame: 0, x: obj.x - 300, y: obj.y, rotation: obj.rotation || 0, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 0 },
      { frame: Math.floor(totalFrames * 0.3), x: obj.x + 20, y: obj.y, rotation: obj.rotation || 0, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 1 },
      { frame: Math.floor(totalFrames * 0.45), x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 1 },
      { frame: totalFrames, x: obj.x, y: obj.y, rotation: obj.rotation || 0, scaleX: obj.scaleX || 1, scaleY: obj.scaleY || 1, opacity: 1 },
    ]
  },
];

export function AnimationPresets({ trigger }) {
  const { state, dispatch } = useAnimation();
  const selectedObj = useSelectedObject();
  const { t } = useI18n();

  const applyPreset = (presetId) => {
    if (!selectedObj) {
      toast.error(t('selectObject'));
      return;
    }
    const preset = presetDefs.find(p => p.id === presetId);
    if (!preset) return;

    const keyframes = preset.generate(selectedObj, state.project.totalFrames);

    dispatch({
      type: 'UPDATE_OBJECT',
      payload: {
        id: selectedObj.id,
        props: { keyframes },
        autoKey: false,
      }
    });

    toast.success(`${t(presetId)} applied`);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm bg-[hsl(220,14%,11%)] border-white/[0.08]" data-testid="presets-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" />
            {t('animPresets')}
          </DialogTitle>
          <DialogDescription className="sr-only">Choose animation preset</DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[350px]">
          <div className="grid grid-cols-2 gap-2">
            {presetDefs.map(preset => (
              <button
                key={preset.id}
                className="flex items-center gap-2.5 px-3 py-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04] hover:border-white/[0.08] transition-all text-left group"
                onClick={() => applyPreset(preset.id)}
                data-testid={`preset-${preset.id}`}
              >
                <div className={`w-7 h-7 rounded-md bg-white/[0.04] flex items-center justify-center group-hover:bg-white/[0.08] transition-colors`}>
                  <preset.icon className={`w-3.5 h-3.5 ${preset.color}`} />
                </div>
                <span className="text-[11px] font-medium">{t(preset.id)}</span>
              </button>
            ))}
          </div>
          {!selectedObj && (
            <p className="text-[10px] text-muted-foreground/40 text-center mt-3 font-mono">
              {t('selectObject')}
            </p>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
